/**
 * Provides the `flattenObjectsByKey` utility function for flattening a potentially nested array of objects based on a specified key, returning a new array containing shallow copies of all objects (parents and children) with the nesting key removed from the copies.
 * @module addParametersToURL
 */
/**
 * Flattens a potentially nested array of objects based on a specified key, returning a new array containing shallow copies of all objects (parents and children) with the nesting key removed from the copies.
 *
 * **Behavior:**
 * - Takes an array of objects (`arr`) where each object might contain a nested array under a specific property key (`key`, defaulting to `'items'`).
 * - Traverses the input array recursively.
 * - For each object encountered:
 * - Creates a *shallow copy* of the object.
 * - Checks if the object has a nested array under the specified `key`.
 * - If a nested array exists:
 *  - The `key` property (containing the nested array) is *removed* from the shallow copy.
 *  - The modified shallow copy (without the nested array) is added to the result list.
 *  - The function then recursively processes each item within the nested array.
 * - If no nested array exists under the specified `key`:
 *  - The unmodified shallow copy is added to the result list.
 *  - The original input array and its objects are never modified (immutability).
 * - Returns a new, single-level array containing the shallow copies of all processed objects.
 *
 * @since 5.10 Initial implementation.
 * @template T - The base type for the objects in the array. Must extend `Record<string, any>`.
 * @template K - The specific key (as a string literal type, e.g., `'items'`, `'children'`) that holds the nested array. Defaults to `'items'`.
 * @param {readonly FlattenObjectsByKeyNestedType<T, K>[]} arr - The array of potentially nested objects to flatten. Marked `readonly` to emphasize input immutability.
 * @param {K} [key='items'] - The property key containing the nested array within objects. Defaults to `'items'`.
 * @return {T[]} A new, flattened array containing shallow copies of all original and nested objects, with the specified nesting `key` removed from the copies. Returns an empty array if the input `arr` is empty.
 *
 * @example Importing the flattenObjectsByKey utility
 * ```typescript
 * import { flattenObjectsByKey } from '@automator/shared/utils/flatten-objects-by-key';
 * ```
 *
 * @example Flattening with default key 'items'
 * Flattens a structure where nesting occurs under the `items` property.
 * ```typescript
 * interface Item {
 *     id: number;
 *     name?: string;
 * }
 *
 * const nestedItems: readonly FlattenObjectsByKeyNestedType<Item, 'items'>[] = [
 *     {
 *         id: 1,
 *         items: [
 *             {
 *                 id: 2,
 *                 items: [
 *                     {
 *                         id: 3,
 *                         name: 'Deep'
 *                     }
 *                 ]
 *             }
 *         ]
 *     },
 *     {
 *         id: 4,
 *         name: 'Top'
 *     }
 * ];
 *
 * flattenObjectsByKey( nestedItems );
 *
 * // => [ { id: 1 }, { id: 2 }, { id: 3, name: 'Deep' }, { id: 4, name: 'Top' } ]
 * // Note: 'items' key is removed from copies of objects 1 and 2.
 * ```
 *
 * @example Flattening with custom key 'children'
 * Flattens a structure using 'children' as the nesting key.
 * ```typescript
 * interface Node {
 *     nodeId: string;
 *     value: number;
 * }
 *
 * const tree: readonly FlattenObjectsByKeyNestedType<Node, 'children'>[] = [
 *     { nodeId: 'A', value: 10, children: [ { nodeId: 'B', value: 20 } ] },
 *     { nodeId: 'C', value: 30 }
 * ];
 *
 * flattenObjectsByKey(tree, 'children');
 *
 * // => [ { nodeId: 'A', value: 10 }, { nodeId: 'B', value: 20 }, { nodeId: 'C', value: 30 } ]
 * // Note: 'children' key is removed from the copy of object 'A'.
 * ```
 *
 * @example Handling empty input array
 * Returns an empty array when the input array is empty.
 * ```typescript
 * const emptyArr: readonly FlattenObjectsByKeyNestedType<{ id: number }, 'items'>[] = [];
 * flattenObjectsByKey( emptyArr );
 * // => []
 * ```
 *
 * @example Handling array with no nesting
 * Returns shallow copies of the original objects when no nesting key is present.
 * ```typescript
 * const flatArr = [ { id: 1 }, { id: 2 } ];
 * const result = flattenObjectsByKey( flatArr );
 * // => [ { id: 1 }, { id: 2 } ]
 * console.log( result[0] === flatArr[0] ); // => false (it's a copy)
 * ```
 *
 * @remarks
 * - The function performs shallow copies of objects. If object properties contain nested objects or arrays (other than the specified nesting key), those nested structures will be shared by reference between the original and the copy.
 * - The original input array and its objects are guaranteed not to be mutated.
 */
export function flattenObjectsByKey(arr, // Input array uses the recursive helper type, marked Readonly.
key = 'items' // Provide default value, assert type K for generic compatibility.
) {
    const result = []; // Initialize array to store the flattened result, typed as T[].
    /**
     * Internal recursive function to process each item.
     * @param {Readonly<FlattenObjectsByKeyNestedType<T, K>>} item - The current object being processed.
     */
    const flatten = (item) => {
        // 1. Create a shallow copy of the current item.
        const itemCopy = { ...item };
        // 2. Check for nested items using the provided key on the copy (or original).
        const nestedItems = itemCopy[key]; // Type inference: nestedItems is FlattenObjectsByKeyNestedType<T, K>[] | undefined | readonly FlattenObjectsByKeyNestedType<T, K>[] | undefined
        if (nestedItems && Array.isArray(nestedItems)) {
            // 3. If nested items exist, remove the nested key *from the copy*.
            delete itemCopy[key]; // Modify the copy, not the original 'item'. This is really important :)
            // 4. Add the modified copy (without the nested key) to the result list.
            result.push(itemCopy);
            // 5. Recurse on the children found in the nestedItems array.
            // We iterate over the original nestedItems (from the copy before delete, or the original item).
            nestedItems.forEach((subItem) => {
                flatten(subItem);
            });
        }
        else {
            // 6. If no nested items, just add the unmodified shallow copy to the result.
            result.push(itemCopy);
        }
    };
    // Iterate through the initial top-level array and start the flattening process for each item.
    arr.forEach(item => flatten(item));
    // Return the populated flat array containing the copies.
    return result;
}
;
